/**
 * When a session needs renewing, and how long is left of one.
 *
 * Plain functions with time as a parameter, because the console has no component tests and this is
 * exactly the logic that needed one: every rule below is a way renewal goes wrong rather than a
 * preference, and each is a case in `sessionClock.test.ts`.
 *
 * Named `sessionClock` rather than `session` deliberately. `lib/session.test.ts` already exists and
 * tests the `session` object that lives in `api.ts`, so a `lib/session.ts` would leave a test file
 * sitting beside a module it does not test - which is worse than a duller name.
 */

/**
 * How much of the idle window may be left before the console renews.
 *
 * Comfortably above the minute of clock skew the API allows, so a renewal never races the skew
 * window and a browser whose clock is a little fast cannot renew a token the server still calls
 * fresh.
 */
export const RENEW_WHEN_REMAINING_MS = 15 * 60 * 1000

/** Milliseconds until the token expires, or null if the expiry cannot be read. */
export function remainingMs(expiresUtc: string | null | undefined, now: number): number | null {
  if (!expiresUtc) return null
  const expires = Date.parse(expiresUtc)
  return Number.isNaN(expires) ? null : expires - now
}

/** Whether the token is already dead. An unreadable expiry is not treated as expired. */
export function expired(expiresUtc: string | null | undefined, now: number): boolean {
  const remaining = remainingMs(expiresUtc, now)
  return remaining !== null && remaining <= 0
}

/**
 * Whether to renew before making this request.
 *
 * Four rules, and two of them are the ones that stop renewal making things worse:
 *
 * - An expiry we cannot read is never renewed. A bad stored identity would otherwise fire a renewal
 *   on every single request, for ever.
 * - A token that has *already* expired is never renewed either. The refresh would carry the dead
 *   token, answer 401 and end the session anyway - so returning true here only adds a doomed round
 *   trip in front of the sign-out, which is latency spent on making the bad news slower.
 */
export function shouldRenew(
  expiresUtc: string | null | undefined,
  now: number,
  threshold: number = RENEW_WHEN_REMAINING_MS
): boolean {
  const remaining = remainingMs(expiresUtc, now)
  if (remaining === null) return false
  return remaining > 0 && remaining <= threshold
}

/**
 * How long is left, for somebody to read.
 *
 * Minutes and seconds rather than a humanised phrase, because the one place this is shown is a
 * support session counting down to its end and "a few minutes" is not something you can plan the
 * next click around. Clamped at zero: a negative countdown reads as a bug rather than as an ended
 * session.
 */
export function countdown(remaining: number | null): string {
  if (remaining === null) return ''
  const seconds = Math.max(0, Math.floor(remaining / 1000))
  const minutes = Math.floor(seconds / 60)
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`
}
